// B-77 — the Cierres bell: the signed-in person's own "Procesar el día" notifications.
//
// The backend writes one when a run the person started ends or fails (`daily/notify.js`), so it is
// there after a reload, after closing Mantis, after entering another day — what the chip beside
// "Nuevo cierre" cannot be. Opening one marks it read, puts the shell on its client and goes to its
// day. The bell used to be a plain link to Alertas; that link is kept at the foot of the list for
// whoever has the section, so nothing that was reachable stops being.
//
// State and fetching are `useAvisosDeProceso`'s; this file only draws.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CheckCircle2, ChevronRight, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger, cn } from "@mantis-sas/ui";

import { haceCuanto, rutaDeAviso, tonoDeAviso } from "../../../utils/cierreAvisos";
import { useClienteCierres } from "./ClienteCierresContext";
import { useAvisosDeProceso } from "./useAvisosDeProceso";

const ICONO = {
  ok: { Icono: CheckCircle2, clase: "text-brinks-success" },
  aviso: { Icono: AlertTriangle, clase: "text-brinks-warning" },
  error: { Icono: XCircle, clase: "text-brinks-danger" },
  neutral: { Icono: Bell, clase: "text-brinks-muted-foreground" },
};

function Aviso({ aviso, onAbrir }) {
  const { Icono, clase } = ICONO[tonoDeAviso(aviso.outcome)];
  return (
    <li>
      <button
        type="button"
        onClick={() => onAbrir(aviso)}
        className={cn(
          "flex w-full items-start gap-2.5 rounded-[8px] px-2.5 py-2 text-left transition hover:bg-brinks-surface-hover",
          !aviso.read && "bg-brinks-surface"
        )}
      >
        <Icono aria-hidden className={cn("mt-0.5 h-4 w-4 shrink-0", clase)} />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cn("min-w-0 flex-1 truncate text-[12.5px] text-brinks-foreground", !aviso.read && "font-semibold")}>
              {aviso.subject}
            </span>
            <span className="shrink-0 text-[11px] text-brinks-muted-foreground">{haceCuanto(aviso.dateCreated)}</span>
          </span>
          <span className="mt-0.5 block text-[12px] leading-snug text-brinks-muted-foreground">{aviso.message}</span>
        </span>
        {!aviso.read ? <span aria-label="Sin leer" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" /> : null}
      </button>
    </li>
  );
}

export function AvisosDeProceso({ token, alertasHabilitadas = false }) {
  const navigate = useNavigate();
  const { elegirCliente, diaEnCurso } = useClienteCierres();
  const { avisos, noLeidos, marcar } = useAvisosDeProceso({ token, refrescar: diaEnCurso?.fin || 0 });
  // Controlled, so going somewhere from the list also closes it.
  const [abierto, setAbierto] = useState(false);

  const ir = (ruta) => {
    setAbierto(false);
    navigate(ruta);
  };

  const abrir = (aviso) => {
    if (!aviso.read) marcar([aviso.id]);
    if (aviso.clientCode) elegirCliente(aviso.clientCode);
    ir(rutaDeAviso(aviso));
  };

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={noLeidos ? `Notificaciones, ${noLeidos} sin leer` : "Notificaciones"}
          className="relative grid h-8 w-8 place-items-center rounded-[8px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover"
        >
          <Bell className="h-[17px] w-[17px]" />
          {noLeidos ? (
            <span
              data-testid="avisos-sin-leer"
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9.5px] font-semibold leading-none text-primary-foreground ring-2 ring-background"
            >
              {noLeidos > 9 ? "9+" : noLeidos}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="brinks-scope font-redhat w-[340px] rounded-[12px] border bg-popover p-0 shadow-lg">
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <p className="text-[12.5px] font-semibold text-brinks-foreground">Notificaciones</p>
          {noLeidos ? (
            <button type="button" onClick={() => marcar()} className="text-[11.5px] font-medium text-brinks-muted-foreground hover:text-brinks-foreground">
              Marcar todo como leído
            </button>
          ) : null}
        </div>

        {avisos.length ? (
          <ul className="max-h-[360px] space-y-0.5 overflow-y-auto p-1.5">
            {avisos.map((aviso) => (
              <Aviso key={aviso.id} aviso={aviso} onAbrir={abrir} />
            ))}
          </ul>
        ) : (
          <p className="px-3 py-6 text-center text-[12.5px] text-brinks-muted-foreground">
            Aquí te avisamos cuando termine un día que hayas procesado.
          </p>
        )}

        {alertasHabilitadas ? (
          <button
            type="button"
            onClick={() => ir("/business/backoffice/alertas")}
            className="flex w-full items-center justify-between border-t px-3 py-2.5 text-[12px] font-medium text-brinks-muted-foreground transition hover:bg-brinks-surface-hover hover:text-brinks-foreground"
          >
            Ver alertas de cierres <ChevronRight className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
